import { useContext, useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react';
import { useNavigate } from 'react-router-dom';
import Chat from '../Chat';
import {
  TransButtonDefaultWithIcon,
  TransButtonLinkWithIcon,
  TransButtonPrimaryWithSpinner,
} from '../../Atoms/Buttons';
import { WindowContext } from '../../Service/Context/WindowContext';

import { FullHeadPane } from '../../Molecules/Pane';
import ChatMessageAttachmentButton from '../../Molecules/Message/ChatMessageAttachmentButton';
import { ConfirmDialog, useModalState } from '../../Molecules/Dialogs';
import { deleteEmail, emailByAction, patchNewDraft, saveDraft, sendMail } from '../../Service/restapi/messageService';
import { useQuery } from '../../Helpers/UrlQuery';
import { FocusedNodeContext } from '../../Service/Context/FocusedNode';
import { getForDraft, getForSend } from '../../Utils/utils';
import { fetchDocumentById } from '../../Service/restapi/documentService';
import { fetchContractById } from '../../Service/restapi/contractService';
import { fetchQuestionnaireById } from '../../Service/restapi/questionnaireService';
import { findUnresolvablePlaceholders } from '../../Service/restapi/placeholderService';
import ScheduleMail from '../../Templates/Emails/ScheduleMail';
import { MessageContext } from '../../Service/Context/MessageContext';
import { ToastS } from '../../Service/ToastS';
import EmailReceiver from './EmailReceiver';
import { ConstantS } from '../../Service/ConstantS';
import { MixpanelS } from '../../Service/MixpanelS';

const NewEmail = ({
  emailAction,
  setEmailAction,
  changeEmailById,
  event,
  customer,
  eventLocation,
  customAttributeValues,
  emailId,
}) => {
  const query = useQuery();
  const navigate = useNavigate();
  const [sendButtonDisabled, setSendButtonDisabled] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);

  const { message, changeMessage, newMessage } = useContext(MessageContext);
  const [isSending, setSending] = useState(false);
  const [isSaving, setSaving] = useState(false);

  const { windowWidth } = useContext(WindowContext);
  const { focusedNode, setFocusedNode } = useContext(FocusedNodeContext);
  const focusedNodeRef = useRef(focusedNode);
  focusedNodeRef.current = focusedNode;
  const { show, toggle } = useModalState();

  const { i18n } = useLingui();

  function newEmailWithDocumentOrContract(apiFunction, entityType, entityId) {
    apiFunction(entityId)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((documentOrContract) => {
            const emailFileDto = {
              entityType,
              entityId,
              fileName: documentOrContract.fileName,
            };
            setEmailAction('NEW');
            patchNewDraft(event ? event.id : undefined, { attachments: [emailFileDto] }).then((resp) => {
              resp.json().then((newEmail) => {
                changeMessage(newEmail);
              });
            });
          });
        } else {
          console.error(`could not load document/contract ${entityId}`);
        }
      })
      .catch((error) => {
        console.error(`could not load document/contract ${entityId}`);
        console.error({ error });
      });
  }

  useEffect(() => {
    const entityType = query.get('entityType');
    const entityId = query.get('entityId');
    if (entityType && entityId) {
      if (entityType === 'contracts') {
        newEmailWithDocumentOrContract(fetchContractById, entityType, entityId);
      } else if (entityType === 'documents') {
        newEmailWithDocumentOrContract(fetchDocumentById, entityType, entityId);
      } else if (entityType === 'questionnaires') {
        newEmailWithDocumentOrContract(fetchQuestionnaireById, entityType, entityId);
      } else {
        console.error(`IllegalArgument ${entityType}`);
      }
    }
  }, []);

  useEffect(() => {
    if (emailAction === 'REPLY' || emailAction === 'EDIT') {
      if (emailId) {
        emailByAction(emailId, emailAction).then((resp) => {
          if (resp.ok) {
            resp.json().then((replyEmailDto) => {
              changeMessage(replyEmailDto);
            });
          }
        });
      }
    } else if (emailAction === 'NEW' && !query.get('entityType') && !query.get('entityId')) {
      newMessage(event?.id);
    }
  }, [emailAction]);

  function send(patch) {
    const m = getForSend(message, patch, i18n, customer, event, eventLocation, customAttributeValues);
    setFocusedNode(undefined);
    m.scheduledMailId = message.scheduledMailId;
    m.related = message.related;
    if (!m.subject) {
      ToastS.warn('subject.required', 'Bitte gib einen Betreff an');
    } else if (!m.to) {
      ToastS.warn('recipient.required', 'Bitte gib einen Empfänger an');
    } else {
      setSendButtonDisabled(true);
      setSending(true);
      ToastS.cleanAll();
      findUnresolvablePlaceholders(m.subject, m.message).then((resp) => {
        if (resp.ok) {
          sendMail(m)
            .then((resp) => {
              if (resp.ok) {
                MixpanelS.track(ConstantS.TrackingEvents.EmailSent);
                ToastS.success('find.unresolved.placeholders', 'E-Mail erfolgreich verschickt');
                setEmailAction(undefined);

                resp.json().then((messageDto) => {
                  changeEmailById(messageDto.id, messageDto);
                  if (event) {
                    navigate(`/events/${event.id}/emails/outbound`);
                  } else {
                    navigate('/postbox/outbound');
                  }
                });
              } else {
                ToastS.generalError();
              }
            })
            .catch(() => ToastS.generalError())
            .then(() => {
              setSendButtonDisabled(false);
              setSending(false);
            });
        } else if (resp.status === 412) {
          setSendButtonDisabled(false);
          setSending(false);
          resp.json().then((json) => {
            json.forEach((placeholder) => {
              const translation = i18n._(placeholder, { 0: 1 });
              ToastS.error(`placeholder.unsatisfied-${translation}`, i18n._(`${translation} nicht gesetzt`));
            });
          });
        }
      });
    }
  }

  function retry(action) {
    if (focusedNodeRef.current) {
      const name = focusedNodeRef.current.getAttribute('name');
      const value = focusedNodeRef.current.innerHTML;
      const patch = {
        [name]: value,
      };
      if (action === 'draft') {
        saveDraftOnServer(patch);
      } else if (action === 'send') {
        send(patch);
      }
    } else if (action === 'draft') {
      saveDraftOnServer();
    } else if (action === 'send') {
      send();
    }
  }

  function saveDraftOnServer(patch) {
    setSaveButtonDisabled(true);
    setSaving(true);
    const m = getForDraft(message, patch, event ? event.id : undefined, i18n);
    saveDraft(m)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((newMessage) => {
            console.log('saved email ', { newMessage });
            if (message.id !== newMessage.id) {
              changeEmailById(newMessage.id, newMessage);
            }

            ToastS.success('save.draft', 'E-Mail erfolgreich gespeichert');
            // setEmailAction(undefined);

            // if (event) {
            //   navigate(`/events/${event.id}/emails/drafts`);
            // } else {
            //   navigate('/postbox/drafts');
            // }
          });
        } else {
          resp.json().then((json) => {
            console.error(json);
            ToastS.error('save.draft', json.message);
          });
          ToastS.error('save.draft', 'E-Mail konnte nicht gespeichert werden');
        }
      })
      .catch((error) => {
        console.error({ error });
        ToastS.error('save.draft', 'E-Mail konnte nicht gespeichert werden');
      })
      .then(() => {
        setSendButtonDisabled(false);
        setSaveButtonDisabled(false);
        setSaving(false);
      });
  }

  function deleteMail(id) {
    if (id || emailId) {
      deleteEmail(id || emailId)
        .then((r) => {
          changeEmailById(id || emailId, undefined);
        })
        .catch(() => {
          console.error(`could not delete email ${id}` || emailId);
        });
    }
  }

  const styles = {
    container: {
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
    },
    emailStyle: {
      fontFamily: 'monospace',
    },
    actions: {
      display: 'flex',
      flex: 1,
      justifyContent: 'space-between',
    },
  };

  if (!emailAction) {
    return null;
  }

  return (
    <FullHeadPane
      style={styles.container}
      head={
        <>
          &nbsp;
          <TransButtonLinkWithIcon
            icon="TIMES"
            className="primary-link"
            onClick={() => setEmailAction(undefined)}
            // text="actions.close"
            style={{ marginRight: 10 }}
            color="default-new"
          />
        </>
      }
      body={
        <>
          <EmailReceiver eventId={event ? event.id : undefined} />
          <Chat
            message={message}
            event={event}
            mainCustomer={customer}
            eventLocation={eventLocation}
            customAttributeValues={customAttributeValues}
          />
        </>
      }
      footer={
        <div style={styles.actions}>
          <div style={{ display: 'flex' }}>
            <TransButtonPrimaryWithSpinner
              isLoading={isSending}
              onClickHandler={!sendButtonDisabled ? () => retry('send') : () => {}}
              disabled={sendButtonDisabled}
              icon="SEND"
              text={windowWidth > 1030 ? 'actions.send' : undefined}
            />{' '}
            <TransButtonPrimaryWithSpinner
              isLoading={isSaving}
              className="primary-link-new"
              onClickHandler={!saveButtonDisabled ? () => retry('draft') : () => {}}
              disabled={saveButtonDisabled}
              icon="SAVE"
              // text={windowWidth > 1030 ? 'actions.save' : undefined}
              style={{ marginLeft: 5 }}
            />{' '}
            <ScheduleMail
              focusedNodeRef={focusedNodeRef}
              message={message}
              changeEmailById={changeEmailById}
              setEmailAction={setEmailAction}
              customer={customer}
              event={event}
              eventLocation={eventLocation}
              customAttributeValues={customAttributeValues}
              saveMailDraftCallback={!saveButtonDisabled ? () => retry('draft') : () => {}}
            />{' '}
            <ChatMessageAttachmentButton event={event} iconOnly={windowWidth < 1000} />
            <ConfirmDialog
              text="common.discard.email.confirm"
              subject="common.discard.email.confirm.head"
              open={show}
              toggle={toggle}
              handler={() => {
                setEmailAction(undefined);
                deleteMail(message.id);
              }}
            />
          </div>
          {windowWidth > 425 && (
            <TransButtonDefaultWithIcon
              icon="TRASH"
              onClickHandler={!sendButtonDisabled ? toggle : {}}
              disabled={sendButtonDisabled}
              // text={windowWidth > 1030 ? 'actions.discard' : undefined}
              style={{ marginRight: 10 }}
              color="default-new"
            />
          )}
        </div>
      }
    />
  );
};

export default NewEmail;
